<script lang="ts">
  /**
   * The admin view (brief §11.6, §18).
   *
   * Two lists and five verbs. Who is waiting to be let in, and what people have complained
   * about; approve, reject, suspend, hide, delete.
   *
   * What is deliberately **not** here is any view of where anybody has been. There is nothing
   * to show: the schema has nowhere to keep a coordinate, so "who was where" is a question
   * this system cannot answer for anybody, including whoever runs it.
   */
  import { type FlagReason, reasonOf } from '$domain/flags.ts';
  import { request } from './api.ts';
  import Avatar from './Avatar.svelte';
  import { looks } from './looks.svelte.ts';
  import { describeError } from './session.svelte.ts';

  type PendingUser = {
    id: string;
    username: string;
    avatarEmoji: string;
    role: string;
    status: string;
    createdAt: string;
    /** Which invite was used, so a name can be placed. */
    inviteCode: string | null;
    /**
     * The password-manager or platform names behind their passkeys.
     *
     * The one genuinely useful signal on this screen (brief §11.6): somebody who registered
     * with a recognisable manager on a phone looks like a person. There is deliberately no IP
     * address here — it is not stored on the account.
     */
    authenticators: string[];
  };

  type Reported = {
    id: string;
    cell: string;
    username: string;
    note: string | null;
    observedAt: string;
    hiddenReason: string | null;
    reports: { username: string; reason: string; createdAt: string }[];
  };

  type FlaggedRequest = {
    id: string;
    reason: FlagReason;
    note: string | null;
    from: string;
    to: string;
    reportedAt: string;
  };

  type ReportedMessage = {
    id: string;
    author: string;
    body: string;
    hidden: boolean;
    reports: number;
    reportedAt: string;
  };

  type Props = { onclose: () => void };
  const { onclose }: Props = $props();

  let tab = $state<'users' | 'reports'>('users');
  let status = $state<'pending' | 'active' | 'suspended'>('pending');
  let users = $state<PendingUser[]>([]);
  let reported = $state<Reported[]>([]);
  let flagged = $state<FlaggedRequest[]>([]);
  let messages = $state<ReportedMessage[]>([]);
  let invite = $state('');
  let busy = $state(false);
  let error = $state('');

  async function loadUsers() {
    try {
      users = (await request<{ users: PendingUser[] }>(`/api/admin/users?status=${status}`)).users;
      error = '';
    } catch (cause) {
      error = describeError(cause);
    }
  }

  async function loadReports() {
    try {
      const queue = await request<{
        sightings: Reported[];
        flagRequests: FlaggedRequest[];
        zoneMessages: ReportedMessage[];
      }>('/api/admin/reports');
      reported = queue.sightings;
      flagged = queue.flagRequests;
      messages = queue.zoneMessages;
      error = '';
    } catch (cause) {
      error = describeError(cause);
    }
  }

  $effect(() => {
    if (tab === 'users') void loadUsers();
    else void loadReports();
  });

  async function act(path: string, method = 'POST') {
    busy = true;
    try {
      await request(path, { method, ...(method === 'POST' ? { body: '{}' } : {}) });
      if (tab === 'users') await loadUsers();
      else await loadReports();
    } catch (cause) {
      error = describeError(cause);
    } finally {
      busy = false;
    }
  }

  /** Takes somebody's picture down (amendment 21); their emoji stands in for it again. */
  async function removePicture(user: PendingUser) {
    busy = true;
    try {
      await request(`/api/admin/users/${user.id}/avatar`, { method: 'DELETE' });
      looks.remember(user.username, { avatar: null });
      error = '';
    } catch (cause) {
      error = describeError(cause);
    } finally {
      busy = false;
    }
  }

  async function mintInvite() {
    busy = true;
    try {
      invite = (
        await request<{ code: string }>('/api/admin/invites', { method: 'POST', body: '{}' })
      ).code;
      error = '';
    } catch (cause) {
      error = describeError(cause);
    } finally {
      busy = false;
    }
  }
</script>

<section class="panel" aria-label="Verwaltung">
  <header>
    <h2>Verwaltung</h2>
    <button class="close" onclick={onclose} aria-label="Schließen">×</button>
  </header>

  <div class="tabs" role="tablist">
    <button role="tab" aria-selected={tab === 'users'} onclick={() => (tab = 'users')}>
      Konten
    </button>
    <button role="tab" aria-selected={tab === 'reports'} onclick={() => (tab = 'reports')}>
      Beschwerden
    </button>
  </div>

  {#if error}<p class="hint error" role="alert">{error}</p>{/if}

  {#if tab === 'users'}
    <nav class="filters" aria-label="Status">
      {#each ['pending', 'active', 'suspended'] as value (value)}
        <button
          aria-pressed={status === value}
          onclick={() => (status = value as typeof status)}
        >
          {value === 'pending' ? 'Wartet' : value === 'active' ? 'Aktiv' : 'Gesperrt'}
        </button>
      {/each}
    </nav>

    <div class="invite">
      <button disabled={busy} onclick={mintInvite}>Einladungscode erzeugen</button>
      {#if invite}<code>{invite}</code>{/if}
    </div>

    {#if users.length === 0}
      <p class="hint">Niemand.</p>
    {:else}
      <ul>
        {#each users as user (user.id)}
          <li>
            <div class="who">
              <span>
                <Avatar username={user.username} emoji={user.avatarEmoji} /> @{user.username}
              </span>
              <small>
                seit {new Date(user.createdAt).toLocaleDateString('de-DE')}
                {#if user.authenticators.length > 0}· {user.authenticators.join(', ')}{/if}
                {#if user.inviteCode}· Invite {user.inviteCode}{/if}
              </small>
            </div>
            <div class="verbs">
              {#if status === 'pending'}
                <button disabled={busy} onclick={() => act(`/api/admin/users/${user.id}/approve`)}>
                  Freischalten
                </button>
                <button
                  class="quiet"
                  disabled={busy}
                  onclick={() => act(`/api/admin/users/${user.id}/reject`)}
                >
                  Ablehnen
                </button>
              {:else if status === 'active'}
                <button
                  class="quiet"
                  disabled={busy}
                  onclick={() => act(`/api/admin/users/${user.id}/suspend`)}
                >
                  Sperren
                </button>
              {:else}
                <button
                  disabled={busy}
                  onclick={() => act(`/api/admin/users/${user.id}/unsuspend`)}
                >
                  Entsperren
                </button>
              {/if}
              {#if (looks.of(user.username)?.avatar ?? null) !== null}
                <button class="quiet" disabled={busy} onclick={() => removePicture(user)}>
                  Bild entfernen
                </button>
              {/if}
            </div>
          </li>
        {/each}
      </ul>
    {/if}
  {:else if reported.length === 0 && flagged.length === 0 && messages.length === 0}
    <p class="hint">Nichts zu tun.</p>
  {:else}
    {#if messages.length > 0}
      <!-- Zonenchat (amendment 16): read by everybody in the district, so two people's reports
           hide a message until it is decided here — deleted for good, or shown again. -->
      <h3 class="subhead">Gemeldete Chat-Nachrichten</h3>
      <ul>
        {#each messages as entry (entry.id)}
          <li>
            <div class="who">
              <span>von @{entry.author}</span>
              <small>
                {entry.reports}× gemeldet, zuletzt {new Date(entry.reportedAt).toLocaleString('de-DE')}
                {#if entry.hidden}· ausgeblendet{/if}
              </small>
              <em>„{entry.body}"</em>
            </div>
            <div class="verbs">
              <!-- One endpoint for both: it drops the reports and shows the message again. -->
              <button
                class="quiet"
                disabled={busy}
                onclick={() => act(`/api/admin/zone-messages/${entry.id}/restore`)}
              >
                {entry.hidden ? 'Wieder zeigen' : 'In Ordnung'}
              </button>
              <button
                class="quiet"
                disabled={busy}
                onclick={() => act(`/api/zone-messages/${entry.id}`, 'DELETE')}
              >
                Löschen
              </button>
            </div>
          </li>
        {/each}
      </ul>
    {/if}
    {#if flagged.length > 0}
      <!-- Flag requests (amendment 13): one person writing to another, reported by the
           recipient. There is nothing to hide or unhide, only to delete or leave. -->
      <h3 class="subhead">Gemeldete Flaggen-Anfragen</h3>
      <ul>
        {#each flagged as entry (entry.id)}
          {@const why = reasonOf(entry.reason)}
          <li>
            <div class="who">
              <span>@{entry.from} → @{entry.to}</span>
              <small>gemeldet {new Date(entry.reportedAt).toLocaleString('de-DE')}</small>
              <span>{why.emoji} {why.label}</span>
              {#if entry.note}<em>„{entry.note}"</em>{/if}
            </div>
            <div class="verbs">
              <button
                class="quiet"
                disabled={busy}
                onclick={() => act(`/api/admin/flag-requests/${entry.id}`, 'DELETE')}
              >
                Löschen
              </button>
            </div>
          </li>
        {/each}
      </ul>
    {/if}
    <ul>
      {#each reported as entry (entry.id)}
        <li>
          <div class="who">
            <span>von @{entry.username}</span>
            <small>
              {new Date(entry.observedAt).toLocaleString('de-DE')}
              {#if entry.hiddenReason}· ausgeblendet ({entry.hiddenReason}){/if}
            </small>
            {#if entry.note}<em>„{entry.note}"</em>{/if}
            <ul class="reasons">
              {#each entry.reports as complaint (complaint.username)}
                <li>@{complaint.username}: {complaint.reason}</li>
              {/each}
            </ul>
          </div>
          <div class="verbs">
            {#if entry.hiddenReason}
              <button
                class="quiet"
                disabled={busy}
                onclick={() => act(`/api/admin/sightings/${entry.id}/unhide`)}
              >
                Wieder zeigen
              </button>
            {:else}
              <button disabled={busy} onclick={() => act(`/api/admin/sightings/${entry.id}/hide`)}>
                Ausblenden
              </button>
            {/if}
            <button
              class="quiet"
              disabled={busy}
              onclick={() => act(`/api/admin/sightings/${entry.id}`, 'DELETE')}
            >
              Endgültig löschen
            </button>
          </div>
        </li>
      {/each}
    </ul>
  {/if}
</section>

<style>
  .panel {
    position: absolute;
    inset: max(3.5rem, env(safe-area-inset-top)) 1rem max(1rem, env(safe-area-inset-bottom));
    z-index: 4;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    max-width: 34rem;
    margin-inline: auto;
    overflow-y: auto;
    padding: 0.75rem 1rem 1rem;
    border: 1px solid var(--border);
    border-radius: 1rem;
    background: var(--card);
    box-shadow: 0 8px 32px rgb(0 0 0 / 30%);
  }

  header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  h2 {
    margin: 0;
    font-size: 1.1rem;
  }

  .close {
    border: 0;
    background: none;
    color: var(--muted);
    font-size: 1.75rem;
    line-height: 1;
    cursor: pointer;
  }

  .tabs,
  .filters {
    display: flex;
    gap: 0.35rem;
  }

  .tabs button,
  .filters button {
    flex: 1;
    padding: 0.4rem;
    border: 1px solid var(--border);
    border-radius: 0.5rem;
    background: var(--bg);
    color: var(--muted);
    font-size: 0.8125rem;
    cursor: pointer;
  }

  .tabs button[aria-selected='true'],
  .filters button[aria-pressed='true'] {
    border-color: var(--accent);
    color: var(--fg);
  }

  .invite {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .invite button {
    padding: 0.4rem 0.7rem;
    border: 1px solid var(--border);
    border-radius: 0.5rem;
    background: var(--bg);
    color: var(--fg);
    font-size: 0.8125rem;
    cursor: pointer;
  }

  code {
    font-size: 0.9375rem;
    letter-spacing: 0.1em;
  }

  ul {
    display: grid;
    gap: 0.5rem;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  ul > li {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.5rem 0;
    border-top: 1px solid var(--border);
  }

  .who {
    display: grid;
    gap: 0.15rem;
    font-size: 0.9375rem;
  }

  small,
  .hint {
    color: var(--muted);
    font-size: 0.75rem;
  }

  em {
    font-size: 0.8125rem;
  }

  .reasons {
    gap: 0.1rem;
    font-size: 0.75rem;
    color: var(--muted);
  }

  .reasons li {
    border: 0;
    padding: 0;
  }

  .verbs {
    display: grid;
    gap: 0.3rem;
  }

  .verbs button {
    padding: 0.35rem 0.6rem;
    border: 1px solid var(--border);
    border-radius: 0.5rem;
    background: var(--bg);
    color: var(--fg);
    font-size: 0.75rem;
    white-space: nowrap;
    cursor: pointer;
  }

  .quiet {
    opacity: 0.7;
  }

  .hint {
    margin: 0;
  }

  .error {
    color: var(--accent);
  }

  .subhead {
    margin: 0.5rem 0 0.25rem;
    font-size: 0.875rem;
    color: var(--muted);
  }
</style>
